// Mapping-Editor fuer den Kaufland-Lister-Detail-Pane (Stufe 4 — KL-005/006/007/078/081/082).
//
// UI-Polish 2026-05-21: card-basiertes layout im vario-design-system. Sticky-Header mit
// progress + save/reset. Field-groups als collapsible cards mit progress-meta. Pro feld
// ein flex-row mit linker meta-spalte (label + key + desc + required-marker) + source-
// dropdown + value-input. Visueller status-stripe links zeigt mapped/required/template-
// status. Vario-CSS-variables statt hardcoded hex.
//
// Konsumiert die drei Stufe-3-Edge-Functions:
//   - kaufland-listing-schema   → Felder pro field-group + allowedValues + Pflicht-Flags
//   - vario-mapping-sources     → gruppierte Vario-Quellen-Liste fuer das Source-Dropdown
//   - kaufland_get_mapping_profile / _save_mapping_profile  → RPCs aus Migration 0077

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { wireTwoStepConfirm } from '../shared/listing-detail-pane.js';

// ---------- Domain ----------

export interface MappingTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  /** Optional callback fuer den Vorschau-Button im Header. Parent tab schaltet auf preview-pane. */
  onPreviewRequested?: () => void;
}

export interface MappingTabListing {
  storefront: string;
  vario_category_id: string | null;
  kaufland_category_id: number | null;
  vario_listing_id: string;
  vario_article_id: string;
}

interface SchemaField {
  key: string;
  apiTarget: string;
  label: string;
  description?: string | null;
  dataType: string;
  required: boolean;
  requiredCondition?: string;
  allowedValues?: Array<{ value: string; label?: string | null; rate?: number | null }>;
  sharedSetId?: number;
  maxLength?: number;
  isMultilingual?: boolean;
  isVariantRelevant?: boolean;
  defaultSourceKey?: string;
  unit?: string | null;
  fieldGroup: string;
}

interface SchemaResp {
  fieldGroups: Array<{ key: string; label: string; description?: string }>;
  fields: SchemaField[];
  meta: {
    cache: { categoryAttributes: { count: number; latestFetchedAt: string | null }; refreshPending: boolean };
    vatIndicatorsCount: number;
    shippingGroupsCount: number;
  };
}

interface SourceDef {
  sourceKey: string;
  path: string;
  dataType: string;
  label: string;
  /** Bei action=list mit listingId/articleId: konkreter Beispielwert aus Vario. */
  exampleValue?: string | number | null;
  isVariantAttribute?: boolean;
  /** Bei article_images: die Vario-DMS-Resource-ID, die hinter dieser Source steckt. */
  varioResourceId?: string;
  /** Bei article_images: ggf. schon hochgeladene Public-URL (preview-tauglich). */
  publicUrl?: string;
}

interface SourcesResp {
  groups: Array<{ key: string; label: string; sources: SourceDef[] }>;
  meta?: {
    varioBaseUrl?: string;
    [key: string]: unknown;
  };
}

interface ExistingFieldMapping {
  kaufland_field_key: string;
  field_group: string;
  source_type: 'vario_path' | 'fixed_value' | 'template' | 'unmapped' | 'vario_dms_resource';
  vario_path: string | null;
  fixed_value: unknown;
  template: string | null;
  fallback: unknown;
  vario_resource_id?: string | null;
  slot_index?: number | null;
}

interface ProfileResp {
  profile: { id: string; storefront: string; locale: string; category_id: number } | null;
  fields: ExistingFieldMapping[];
  variants: unknown[];
}

interface FieldAlias {
  key: string;
  fieldGroup: string;
  apiTarget: string;
}

/** Display-Field: nach `key` dedupliziertes SchemaField, mit Aliases fuer Save-Fanout.
 *  Wenn dasselbe key in mehreren API-Groups vorkommt (z.B. 'ean' in product_data_basic
 *  UND in offer), wird im UI nur ein Eintrag gezeigt; beim Save schreiben wir den gleichen
 *  Mapping fuer jeden Alias-Eintrag (eine Zeile pro field_group). */
interface DisplayField extends SchemaField {
  aliases: FieldAlias[];
  originalFieldGroup: string;
}

/** Niedrigerer Wert = hoehere Priority. Bei Konflikt gewinnt das hoeher-priorisierte
 *  fieldGroup als "anzeige-group" + Label/Description-Quelle. */
const GROUP_PRIORITY: Record<string, number> = {
  product_data_basic: 1,
  // images: entfernt 2026-05-24 — Bilder gehen ueber den Lister-Tab "Bilder", nicht
  // mehr ueber picture_<n>-Mappings.
  category_required_attributes: 3,
  category_optional_attributes: 4,
  gpsr: 5,
  compliance: 6,
  variants: 7,
  offer: 8,
};

const GROUP_LABELS: Record<string, string> = {
  product_data_basic: 'Basisdaten',
  category_required_attributes: 'Kategorie',
  category_optional_attributes: 'Kategorie (opt)',
  gpsr: 'GPSR',
  compliance: 'Compliance',
  variants: 'Varianten',
  offer: 'Angebot',
};

/**
 * Klassifiziert Felder, die typischerweise in Vario am Artikel gepflegt werden, fuer die
 * neue UI-Gruppe "Vario Felder". Match nach key (deterministisch fuer top-level schema
 * fields) und nach label-pattern (fuer Kaufland-Kategorie-Attribute, deren key je
 * Kategorie variiert und nur als deutscher Label-Text stabil bleibt). Reihenfolge:
 *   - mpn, list_price, id_shipping_group, product_safety_contact.* (top-level)
 *   - Grundpreiseinheit/-menge, Maße (Länge/Breite/Höhe/Tiefe), Gewicht, Stückzahl,
 *     Online-Bestellnummer (per label, da als category_attribute reinkommend)
 */
// Optionale Kaufland-Attribute (per stabilem Namen = field.key), die Vario-gemappt sind.
// Spiegel: SQL kaufland.optional_attr_is_vario_mapped (Migration 0124/0128) +
// kaufland-listing-schema VARIO_MAPPED_ATTRIBUTE_NAMES. Bei Aenderung alle drei angleichen.
// 'length' seit 0128 NICHT mehr Vario-gemappt: Direktwert im Optionale-Felder-Tab.
const VARIO_OPTIONAL_ATTR_KEYS = new Set<string>([
  'mpn', 'real_mgb_artnum', 'quantity', 'weight', 'base_price_unit', 'content_volume',
  'product_dimensions', 'width', 'height', 'depth', 'diameter',
]);

// Basis-Schema-Felder, die als Direktwert im Tab "Optionale Felder" gepflegt werden
// (Default geteilt + Artikel-Override) und daher NICHT mehr im Mapping-Editor erscheinen.
// Spiegel: Migration 0126/0127 (kaufland_list_optional_field_overrides injiziert sie als base:*)
// + optional-fields.ts. Werden in loadAndRender aus displayFields herausgefiltert.
// list_price (UVP) ist hier NICHT — es bleibt im Editor als Vario-Feld (siehe isVarioField).
const BASE_OPTIONAL_FIELD_KEYS = new Set<string>([
  'short_description', 'minimum_price', 'note',
]);

function isVarioField(f: SchemaField): boolean {
  if (VARIO_OPTIONAL_ATTR_KEYS.has(f.key)) return true;
  if (f.key === 'mpn' || f.key === 'id_shipping_group' || f.key === 'list_price') return true;
  // UVP (list_price) -> Vario-Feld (oben, mapbar aus Vario). Kurzbeschreibung
  // (short_description), Mindestpreis (minimum_price), Kunden-Hinweis (note) sind weder Vario-
  // noch Mapping-Felder — sie werden komplett aus dem Editor gefiltert
  // (BASE_OPTIONAL_FIELD_KEYS) und im Tab "Optionale Felder" gepflegt.
  // Varianten-Felder (Variantenvater): Parent-EAN + Varianten-Attribute.
  if (f.key === 'parent_ean' || f.key === 'variant_attributes') return true;
  if (f.key.startsWith('product_safety_contact.')) return true;
  const lbl = f.label.toLowerCase();
  if (lbl.includes('grundpreis')) return true;
  if (lbl.includes('maße') || lbl.includes('länge') || lbl.includes('breite') || lbl.includes('höhe') || lbl.includes('tiefe')) return true;
  if (lbl.includes('gewicht')) return true;
  if (lbl.includes('stückzahl')) return true;
  if (lbl.includes('hersteller-artikelnummer')) return true;
  if (lbl.includes('bestellnummer')) return true;
  return false;
}

function dedupFieldsByKey(fields: SchemaField[]): DisplayField[] {
  const byKey = new Map<string, DisplayField>();
  for (const f of fields) {
    const existing = byKey.get(f.key);
    const alias: FieldAlias = { key: f.key, fieldGroup: f.fieldGroup, apiTarget: f.apiTarget };
    if (!existing) {
      byKey.set(f.key, { ...f, aliases: [alias], originalFieldGroup: f.fieldGroup });
      continue;
    }
    existing.aliases.push(alias);
    const eP = GROUP_PRIORITY[existing.originalFieldGroup] ?? 99;
    const fP = GROUP_PRIORITY[f.fieldGroup] ?? 99;
    if (fP < eP) {
      // f hat hoehere prio — uebernimm dessen meta (label, description, allowedValues, etc.)
      // aber merge die aliasliste
      byKey.set(f.key, { ...f, aliases: existing.aliases, originalFieldGroup: f.fieldGroup });
    }
    const merged = byKey.get(f.key)!;
    merged.required = merged.required || f.required;
  }
  return [...byKey.values()];
}

interface EditorState {
  schema: SchemaResp;
  sources: SourcesResp;
  profile: ProfileResp;
  /** Deduplizierte + nach Pflicht sortierte Felder fuer das Rendering. */
  displayFields: DisplayField[];
  mappings: Map<string, ExistingFieldMapping>;
  /** Listing-spezifische Feld-Overrides (Migration 0129): kaufland_field_key -> fester Wert.
   *  Gesetzt = sticht die Profil-Quelle nur fuer dieses Listing. */
  overrides: Map<string, unknown>;
  dirty: boolean;
}

// ---------- Locale-Ableitung ----------

const STOREFRONT_LOCALES: Record<string, string> = {
  de: 'de-DE', at: 'de-AT', cz: 'cs-CZ', sk: 'sk-SK',
  pl: 'pl-PL', fr: 'fr-FR', it: 'it-IT',
};

function localeFor(storefront: string): string {
  return STOREFRONT_LOCALES[storefront] ?? 'de-DE';
}

// ---------- Edge-Function-Aufruf ----------

async function callFn<T>(
  ctx: MappingTabCtx,
  name: string,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ctx.session.token}`,
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// ---------- Component-scoped CSS ----------

// Brand-Farben + neutrale Layer kommen aus dem GO-Brand-Layer (bridge.css).
// Hier nur Lister-Editor-spezifische Klassen.
const COMPONENT_STYLES = `
  .kl-map-shell { display: flex; flex-direction: column; gap: 14px; padding: 16px; padding-bottom: 0; }

  /* ── Header: Titel/Subtitle + Action-Buttons + Progress-Bar ──────────────── */
  .kl-map-header {
    background: var(--v-surface, #fff);
    padding: 14px 16px;
    border: 1px solid #E4E7EC;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(16,24,40,0.04);
  }
  .kl-map-title { font-size: 16px; font-weight: 600; color: #101828; }
  .kl-map-subtitle { font-size: 12px; color: #475467; margin-top: 2px; }

  .kl-map-progress-row {
    display: flex; align-items: center; gap: 10px;
    margin-top: 12px;
  }
  .kl-map-progress-bar {
    flex: 1 1 auto;
    height: 8px;
    background: #E4E7EC;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
  }
  .kl-map-progress-seg-ok { background: #15803D; transition: width 220ms ease-out; }
  .kl-map-progress-seg-warn { background: var(--go-orange, #1A1D21); transition: width 220ms ease-out; }
  .kl-map-progress-text {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    color: #475467;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  /* ── Quick-Filter Toolbar ───────────────────────────────────────────────── */
  .kl-map-quickbar {
    display: flex; align-items: center; gap: 10px;
    margin-top: 10px;
  }
  .kl-map-quickbar .kl-map-search {
    flex: 1 1 auto;
    position: relative;
    display: flex; align-items: center;
    background: #fff;
    border: 1px solid #D0D5DD;
    border-radius: 6px;
    height: 32px;
    padding: 0 10px;
    gap: 6px;
  }
  .kl-map-search > .v-icon { color: #98A2B3; font-size: 12px; }
  .kl-map-search > input {
    flex: 1 1 auto;
    border: 0; outline: none; background: transparent;
    height: 100%; font: inherit; font-size: 12.5px; color: #101828;
  }
  .kl-map-toggle {
    flex: 0 0 auto;
    display: inline-flex; align-items: center; gap: 6px;
    height: 32px; padding: 0 12px;
    background: #fff; border: 1px solid #D0D5DD;
    border-radius: 999px;
    font-size: 12px; color: #475467; cursor: pointer;
    transition: background 140ms ease, color 140ms ease, border-color 140ms ease;
  }
  .kl-map-toggle:hover { border-color: #98A2B3; }
  .kl-map-toggle[data-active="1"] {
    background: rgba(26,29,33,0.08);
    border-color: var(--go-orange, #1A1D21);
    color: var(--go-orange, #1A1D21);
  }
  .kl-map-toggle .v-icon { font-size: 11px; }
  .kl-map-automap-btn {
    flex: 0 0 auto;
    display: inline-flex; align-items: center; gap: 6px;
    height: 32px; padding: 0 12px;
    background: #fff; border: 1px solid #D0D5DD;
    border-radius: 6px;
    font-size: 12px; color: #344054; cursor: pointer; font-family: inherit;
    transition: border-color 140ms ease, background 140ms ease;
  }
  .kl-map-automap-btn:hover { border-color: #98A2B3; background: #F9FAFB; }
  .kl-map-automap-btn[disabled] { opacity: 0.55; cursor: not-allowed; }
  .kl-map-automap-btn .v-icon { font-size: 12px; color: var(--go-orange, #1A1D21); }

  /* ── Action-Buttons (Header) Button-Hierarchie ──────────────────────────── */
  .kl-map-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  .kl-map-btn {
    display: inline-flex; align-items: center; gap: 6px;
    height: 32px; padding: 0 14px;
    border-radius: 6px; cursor: pointer; font: inherit; font-size: 12.5px;
    transition: filter 140ms ease, background 140ms ease, border-color 140ms ease;
  }
  .kl-map-btn--primary {
    background: linear-gradient(180deg, #1A1D21 0%, #0D0F11 100%);
    color: #fff; border: 1px solid #0D0F11;
    box-shadow: 0 1px 2px rgba(26,29,33,0.20);
  }
  .kl-map-btn--primary:hover { filter: brightness(0.96); }
  .kl-map-btn--primary[disabled] { opacity: 0.55; cursor: not-allowed; filter: none; }
  .kl-map-btn--secondary {
    background: #fff; color: #344054; border: 1px solid #D0D5DD;
  }
  .kl-map-btn--secondary:hover { border-color: #98A2B3; background: #F9FAFB; }
  .kl-map-btn--ghost {
    background: transparent; color: #475467; border: 1px solid transparent;
  }
  .kl-map-btn--ghost:hover { border-color: #D0D5DD; background: #F9FAFB; }
  .kl-map-btn--icon {
    width: 32px; height: 32px; padding: 0; justify-content: center;
    background: transparent; color: #475467; border: 1px solid transparent;
  }
  .kl-map-btn--icon:hover { border-color: #D0D5DD; background: #F9FAFB; }
  .kl-map-save-dirty::after {
    content: '*';
    margin-left: 2px;
    font-weight: 700;
  }

  /* ── Section (Pflichtfelder / Optionale Felder) ─────────────────────────── */
  .kl-map-group {
    border: 1px solid #E4E7EC;
    border-radius: 10px;
    background: var(--v-surface, #fff);
    overflow: hidden;
  }
  .kl-map-group > summary {
    list-style: none;
    cursor: pointer;
    padding: 12px 14px;
    background: #F9FAFB;
    display: flex; align-items: center; gap: 10px;
    border-bottom: 1px solid transparent;
    transition: border-color 160ms ease-out;
    user-select: none;
  }
  .kl-map-group[open] > summary { border-bottom-color: #E4E7EC; }
  .kl-map-group > summary::-webkit-details-marker { display: none; }
  .kl-map-group-chevron {
    transition: transform 220ms ease-out;
    font-size: 12px; color: #667085; flex-shrink: 0;
  }
  .kl-map-group[open] > summary .kl-map-group-chevron { transform: rotate(90deg); }
  .kl-map-group-title { font-weight: 600; font-size: 13.5px; flex: 1; color: #101828; }
  .kl-map-group-meta {
    font-size: 11.5px; color: #475467;
    display: flex; gap: 10px; align-items: center; flex-shrink: 0;
  }
  .kl-map-group-mini-progress {
    width: 60px; height: 4px;
    background: #E4E7EC; border-radius: 2px; overflow: hidden;
    display: flex; flex-shrink: 0;
  }
  .kl-map-group-mini-progress > div { transition: width 220ms ease-out; }
  .kl-map-group-mini-count {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px; color: #475467; white-space: nowrap;
  }

  .kl-map-group-body { padding: 0; }
  .kl-map-group-body > .kl-map-field + .kl-map-field { border-top: 1px solid #F2F4F7; }

  /* ── Field-Row ───────────────────────────────────────────────────────────── */
  .kl-map-field {
    display: grid;
    /* Drei gleichwertige Spalten: Feldanzeige · Mapping-Dropdown · manuelle Eingabe. */
    grid-template-columns: minmax(200px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr);
    gap: 14px;
    padding: 12px 14px 12px 16px;
    position: relative;
    align-items: start;
    transition: background 160ms ease-out;
  }
  .kl-map-field-source { align-self: start; }
  .kl-map-field[hidden] { display: none; }
  .kl-map-field::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: #E4E7EC;
  }
  .kl-map-field[data-status="mapped"]::before  { background: #15803D; }
  .kl-map-field[data-status="required-empty"]::before { background: var(--go-orange, #1A1D21); }
  .kl-map-field[data-status="fixed"]::before    { background: #15803D; }
  .kl-map-field[data-status="template"]::before { background: #15803D; }

  /* Hellgelb fuer nicht-gemappte Pflichtfelder, sehr leicht fuer optionale */
  .kl-map-field[data-status="required-empty"] { background: #FFFBEB; }
  .kl-map-field[data-status="empty"]:not([data-status="required-empty"]) { background: #FAFAFA; }

  .kl-map-field-meta { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .kl-map-field-label-row { display: flex; align-items: baseline; gap: 6px; }
  .kl-map-field-label {
    font-weight: 600; font-size: 14px; color: #101828;
    cursor: help;
  }
  .kl-map-field-req {
    color: var(--go-orange, #1A1D21);
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
  }
  .kl-map-field-info {
    color: #98A2B3;
    font-size: 12px;
    cursor: help;
  }
  .kl-map-field-typeline {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    color: #667085;
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }
  .kl-map-field-desc {
    font-size: 11.5px;
    color: #475467;
    line-height: 1.35;
    margin-top: 2px;
  }
  .kl-map-field-conditional {
    font-size: 11px;
    color: #B58A14;
    font-style: italic;
    margin-top: 2px;
  }
  .kl-map-field-allowed {
    font-size: 11px;
    color: #98A2B3;
    margin-top: 2px;
  }

  /* Pfeil zw. Source und Target */
  .kl-map-field-arrow {
    color: #98A2B3;
    font-size: 14px;
    text-align: center;
  }

  /* Mapping-Source-Dropdown */
  .kl-map-field-source select {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #D0D5DD;
    border-radius: 6px;
    background: #fff;
    font: inherit;
    font-size: 13px;
    color: #101828;
    cursor: pointer;
    transition: border-color 140ms ease, box-shadow 140ms ease;
  }
  .kl-map-field-source select:hover { border-color: #98A2B3; }
  .kl-map-field-source select:focus {
    outline: 0;
    border-color: var(--go-orange, #1A1D21);
    box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
  }

  /* Value-Input (rechte Spalte) */
  .kl-map-field-value-shell { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .kl-map-field-value-shell input,
  .kl-map-field-value-shell textarea,
  .kl-map-field-value-shell select {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #D0D5DD;
    border-radius: 6px;
    background: #fff;
    font: inherit;
    font-size: 13px;
    color: #101828;
  }
  .kl-map-field-value-shell textarea {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    min-height: 64px;
    height: auto;
    padding: 8px 12px;
    resize: vertical;
  }
  .kl-map-field-value-shell input:focus,
  .kl-map-field-value-shell textarea:focus,
  .kl-map-field-value-shell select:focus {
    outline: 0;
    border-color: var(--go-orange, #1A1D21);
    box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
  }
  .kl-map-field-value-hint { font-size: 11px; color: #98A2B3; }
  .kl-map-field-value-vario {
    font-size: 11.5px; color: #475467;
    display: flex; align-items: center; gap: 6px; padding: 4px 0;
  }
  .kl-map-field-value-vario code {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px; color: #1849A9;
  }
  .kl-map-field-value-empty {
    font-size: 12px; color: #98A2B3; font-style: italic; padding: 4px 0;
  }

  /* Manuelle Eingabe / Listing-Override (Spalte 3) */
  .kl-map-field-override { display: flex; flex-direction: column; gap: 3px; }
  .kl-map-field-override-input.is-set { border-color: #F59E0B !important; }
  .kl-map-field-override-cap {
    font-size: 10px; color: #98A2B3; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .kl-map-field-override-cap.is-set { color: #B45309; font-weight: 600; }

  @media (max-width: 900px) {
    .kl-map-field {
      grid-template-columns: 1fr;
      gap: 8px;
    }
    .kl-map-field-arrow { display: none; }
  }

  /* ── Action-Bar (Speichern/Reset) ───────────────────────────────────────── */
  /* Kein position:sticky — die Leiste soll im normalen Fluss am Ende des
     Mapping-Editors bleiben und mit dem Inhalt scrollen, nicht am Rand kleben. */
  .kl-map-sticky-actions {
    background: #fff;
    border-top: 1px solid #E4E7EC;
    padding: 14px 16px;
    margin: 0 -16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
`;

// ---------- Public entrypoint ----------

export async function renderMappingTab(
  host: HTMLElement,
  ctx: MappingTabCtx,
  listing: MappingTabListing,
): Promise<void> {
  host.innerHTML = `
    <style>${COMPONENT_STYLES}</style>
    <div class="v-pa-md" style="display:flex;flex-direction:column;align-items:center;gap:0.5em;color:var(--v-color-text-muted,#666);">
      <span class="v-icon fa-regular fa-spinner-third fa-spin" style="font-size:1.5em;"></span>
      <span>Lade Schema und Quellen …</span>
    </div>
  `;

  const categoryId = listing.kaufland_category_id;
  if (!categoryId) {
    host.innerHTML = `
      <style>${COMPONENT_STYLES}</style>
      <div class="v-pa-md">
        <div class="v-alert v-alert--info">
          <strong>Mapping noch nicht möglich.</strong><br>
          Dieses Listing hat noch keine Kaufland-Kategorie. Bitte zuerst im Vario-Listing-Slot
          (Verkaufskanäle-Reiter am Artikel) eine Kategorie zuweisen.
        </div>
      </div>`;
    return;
  }

  const storefront = listing.storefront;
  const locale = localeFor(storefront);

  const [schemaResp, sourcesResp, profileResp, overridesResp] = await Promise.all([
    callFn<SchemaResp>(ctx, 'kaufland-listing-schema', { storefront, locale, categoryId }),
    callFn<SourcesResp>(ctx, 'vario-mapping-sources', {
      action: 'list',
      storefront,
      articleId: listing.vario_article_id,
      listingId: listing.vario_listing_id,
    }),
    ctx.supabase.rpc('kaufland_get_mapping_profile', { _storefront: storefront, _locale: locale, _category_id: categoryId }),
    // Listing-spezifische Overrides (Migration 0129) — fail-soft, blockiert den Editor nicht.
    ctx.supabase.rpc('kaufland_list_listing_field_overrides', { _storefront: storefront, _vario_article_id: listing.vario_article_id }),
  ]);

  if (!schemaResp.ok) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Schema-Ladung fehlgeschlagen: ${escapeHtml(schemaResp.error)}</div>`;
    return;
  }
  if (!sourcesResp.ok) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Quellen-Ladung fehlgeschlagen: ${escapeHtml(sourcesResp.error)}</div>`;
    return;
  }
  if (profileResp.error) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Profil-Ladung fehlgeschlagen: ${escapeHtml(profileResp.error.message)}</div>`;
    return;
  }

  const schema = schemaResp.value;
  const sources = sourcesResp.value;
  const profileRaw = (profileResp.data ?? { profile: null, fields: [], variants: [] }) as ProfileResp;
  const profile: ProfileResp = {
    profile: profileRaw.profile ?? null,
    fields: Array.isArray(profileRaw.fields) ? profileRaw.fields : [],
    variants: Array.isArray(profileRaw.variants) ? profileRaw.variants : [],
  };

  // Dedup nach `key`, dann Pflicht nach oben sortieren (innerhalb gruppe-prioritaet).
  // Basis-Felder (Kurzbeschreibung/UVP/Mindestpreis/Kunden-Hinweis) werden hier entfernt —
  // sie gehoeren in den Tab "Optionale Felder", nicht in den Mapping-Editor.
  const displayFields = dedupFieldsByKey(schema.fields).filter((f) => !BASE_OPTIONAL_FIELD_KEYS.has(f.key));
  displayFields.sort((a, b) => {
    if (a.required !== b.required) return a.required ? -1 : 1;
    const aP = GROUP_PRIORITY[a.originalFieldGroup] ?? 99;
    const bP = GROUP_PRIORITY[b.originalFieldGroup] ?? 99;
    return aP - bP;
  });

  const mappings = new Map<string, ExistingFieldMapping>();
  for (const f of displayFields) {
    const existing = profile.fields.find((m) => m.kaufland_field_key === f.key);
    if (existing) {
      mappings.set(f.key, existing);
    } else if (f.key === 'product_dimensions') {
      // Kaufland-Feld "Maße" (product_dimensions) erwartet Freitext "B x H x T" + Einheit.
      // Default = kombinierte Maße-Quelle (netto): resolveSource baut "Breite x Höhe x Tiefe cm"
      // aus netMetric.sizeX/Y/Z + sizeUnit.abbreviation. Im Dropdown waehlbar als
      // "Maße komplett (B×H×T, netto)".
      mappings.set(f.key, {
        kaufland_field_key: f.key,
        field_group: f.originalFieldGroup,
        source_type: 'vario_path',
        vario_path: 'article.netMetric.dimensions',
        fixed_value: null,
        template: null,
        fallback: null,
      });
    } else if (f.defaultSourceKey) {
      mappings.set(f.key, {
        kaufland_field_key: f.key,
        field_group: f.originalFieldGroup,
        source_type: 'vario_path',
        vario_path: f.defaultSourceKey,
        fixed_value: null,
        template: null,
        fallback: null,
      });
    } else if (f.allowedValues && f.allowedValues.length > 0) {
      // Enum-felder ohne sinnvollen vario-default (z.B. vat_indicator, condition, id_shipping_group)
      // direkt als fixed_value mit leerem wert vorbelegen — der renderer zeigt dann sofort das
      // dropdown statt erst die "Fester Wert"-auswahl im source-dropdown zu verlangen.
      mappings.set(f.key, {
        kaufland_field_key: f.key,
        field_group: f.originalFieldGroup,
        source_type: 'fixed_value',
        vario_path: null,
        fixed_value: '',
        template: null,
        fallback: null,
      });
    }
  }

  // Listing-Overrides in eine Map kaufland_field_key -> Wert (Migration 0129). Fail-soft.
  const overrides = new Map<string, unknown>();
  if (overridesResp.error) {
    console.warn('[kaufland-list-mapping] overrides load failed:', overridesResp.error.message);
  } else {
    for (const row of (overridesResp.data as Array<{ kaufland_field_key: string; value: unknown }> | null) ?? []) {
      overrides.set(row.kaufland_field_key, row.value);
    }
  }

  const state: EditorState = { schema, sources, profile, displayFields, mappings, overrides, dirty: false };
  renderEditor(host, ctx, listing, state);
}

// ---------- Renderer ----------

function renderEditor(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState): void {
  const mappedCount = countMapped(state);
  const requiredOpen = countRequiredOpen(state);
  const totalFields = state.displayFields.length;
  const profileExists = state.profile.profile !== null;

  // Vario-Felder werden VOR der Pflicht/Optional-Aufteilung extrahiert — ein Feld
  // soll in genau einer Gruppe erscheinen, sonst doppelter Editor + doppelte
  // mini-progress-Zaehlung. Pflichtflag bleibt erhalten (Stripe + Star-Marker im
  // FieldRow zeigen weiterhin Pflicht-Status innerhalb der Vario-Gruppe).
  const varioFields = state.displayFields.filter((f) => isVarioField(f));
  const remainingFields = state.displayFields.filter((f) => !isVarioField(f));
  const requiredFields = remainingFields.filter((f) => f.required);
  const optionalFields = remainingFields.filter((f) => !f.required);

  const refreshPendingNote = state.schema.meta.cache.refreshPending
    ? renderAlert('info', 'Kaufland-Schema-Cache wird aktualisiert (Hintergrund-Job). Beim nächsten Reload sind die Felder frisch.')
    : '';
  const sgEmpty = state.schema.meta.shippingGroupsCount === 0
    ? renderAlert('info', 'Keine Versandgruppen im Cache — bitte vorher in den Einstellungen „Versandgruppen aktualisieren".')
    : '';

  const requiredTotal = requiredFields.length;
  const requiredMapped = requiredFields.filter((f) => isMapped(state.mappings.get(f.key))).length;
  const optionalTotal = optionalFields.length;
  const optionalMapped = optionalFields.filter((f) => isMapped(state.mappings.get(f.key))).length;
  const varioTotal = varioFields.length;
  const varioMapped = varioFields.filter((f) => isMapped(state.mappings.get(f.key))).length;
  // Vario-Article-URL ist der einzige verfuegbare "External-Link" auf dieser Ebene —
  // Kaufland-Backend-URL wuerde kaufland_unit_id brauchen, die wir hier (Mapping-Editor)
  // nicht im Context haben. Fallback: disabled-Icon-Button.
  const externalHref = state.sources.meta?.varioBaseUrl
    ? `${state.sources.meta.varioBaseUrl}/products/articles/${encodeURIComponent(listing.vario_article_id)}/detail`
    : null;

  host.innerHTML = `
    <style>${COMPONENT_STYLES}</style>
    <div class="kl-map-shell">

      <div class="kl-map-header">
        <div style="display:flex;align-items:flex-start;gap:12px;">
          <div style="flex:1;min-width:0;">
            <div class="kl-map-title">Mapping-Editor</div>
            <div class="kl-map-subtitle">
              Storefront <strong>${escapeHtml(listing.storefront.toUpperCase())}</strong>
              · Kategorie <strong>${listing.kaufland_category_id}</strong>
              · ${state.schema.meta.cache.categoryAttributes.count} Kategorie-Attribute
              · ${state.schema.meta.vatIndicatorsCount} VAT-Indikatoren
              · ${state.schema.meta.shippingGroupsCount} Versandgruppen
              ${profileExists ? '· <span style="color:#15803D;">gespeichert</span>' : '· <span style="color:#475467;">neu</span>'}
            </div>
          </div>
          <div class="kl-map-actions">
            ${externalHref
              ? `<a href="${escapeHtml(externalHref)}" target="_blank" rel="noopener" class="kl-map-btn kl-map-btn--icon" title="Vario-Artikel öffnen"><span class="v-icon fa-light fa-arrow-up-right-from-square"></span></a>`
              : `<button type="button" class="kl-map-btn kl-map-btn--icon" disabled title="Externer Link nicht verfügbar"><span class="v-icon fa-light fa-arrow-up-right-from-square"></span></button>`}
            <button type="button" class="kl-map-btn kl-map-btn--ghost" data-action="reset" title="Änderungen verwerfen und Profil neu laden">
              <span class="v-icon fa-light fa-rotate-left"></span>Zurücksetzen
            </button>
            <button type="button" class="kl-map-btn kl-map-btn--secondary" data-action="preview" id="kl-map-preview" title="Push-Vorschau anzeigen — bei ungespeicherten Änderungen wird vorher automatisch gespeichert">
              <span class="v-icon fa-light fa-eye"></span>Vorschau
            </button>
            <button type="button" class="kl-map-btn kl-map-btn--primary" data-action="save" id="kl-map-save">
              <span class="v-icon fa-light fa-floppy-disk"></span>Speichern
            </button>
          </div>
        </div>

        ${renderProgressBar(mappedCount, totalFields, requiredOpen)}

        <div class="kl-map-quickbar">
          <div class="kl-map-search">
            <span class="v-icon fa-light fa-magnifying-glass"></span>
            <input type="search" id="kl-map-search" placeholder="Feldname suchen…" autocomplete="off" />
          </div>
          <button type="button" class="kl-map-toggle" id="kl-map-toggle-required" title="Nur Pflichtfelder zeigen, die noch nicht gemappt sind">
            <span class="v-icon fa-light fa-circle"></span>
            Nur Pflicht offen
          </button>
          <button type="button" class="kl-map-automap-btn" id="kl-map-automap" title="Felder mit passendem Quellnamen automatisch zuordnen">
            <span class="v-icon fa-light fa-wand-magic-sparkles"></span>
            Auto-Map Vorschläge
          </button>
        </div>

        ${refreshPendingNote ? `<div style="margin-top:10px;">${refreshPendingNote}</div>` : ''}
        ${sgEmpty ? `<div style="margin-top:10px;">${sgEmpty}</div>` : ''}
      </div>

      <div id="kl-map-groups" style="display:flex;flex-direction:column;gap:12px;">
        ${renderDisplayGroup({ key: 'required', label: 'Pflichtfelder', description: 'Alle Pflichtangaben für Kaufland — dedupliziert' }, requiredFields, state, true, requiredMapped, requiredTotal)}
        ${renderDisplayGroup({ key: 'vario', label: 'Vario Felder', description: 'Werte, die typischerweise in Vario am Artikel gepflegt werden' }, varioFields, state, true, varioMapped, varioTotal)}
        ${renderDisplayGroup({ key: 'optional', label: 'Optionale Felder', description: 'Sinnvoll, aber nicht erzwungen' }, optionalFields, state, false, optionalMapped, optionalTotal)}
      </div>

      <div class="kl-map-sticky-actions">
        <button type="button" class="kl-map-btn kl-map-btn--ghost" data-action="reset-bottom" title="Änderungen verwerfen">
          <span class="v-icon fa-light fa-rotate-left"></span>Zurücksetzen
        </button>
        <button type="button" class="kl-map-btn kl-map-btn--secondary" data-action="preview-bottom" title="Push-Vorschau">
          <span class="v-icon fa-light fa-eye"></span>Vorschau
        </button>
        <button type="button" class="kl-map-btn kl-map-btn--primary" data-action="save-bottom" id="kl-map-save-bottom">
          <span class="v-icon fa-light fa-floppy-disk"></span>Speichern
        </button>
      </div>

    </div>
  `;

  wireEvents(host, ctx, listing, state);
}

/** Horizontale 3-Segment-Progressbar. Gruen = gemappt, orange = Pflicht offen, rest grau. */
function renderProgressBar(mapped: number, total: number, requiredOpen: number): string {
  if (total === 0) return '';
  const okPct = (mapped / total) * 100;
  const warnPct = (requiredOpen / total) * 100;
  return `
    <div class="kl-map-progress-row">
      <div class="kl-map-progress-bar" role="progressbar" aria-valuenow="${mapped}" aria-valuemin="0" aria-valuemax="${total}">
        <div class="kl-map-progress-seg-ok" style="width: ${okPct}%;"></div>
        <div class="kl-map-progress-seg-warn" style="width: ${warnPct}%;"></div>
      </div>
      <div class="kl-map-progress-text">${mapped} / ${total} Felder gemappt${requiredOpen > 0 ? ` · ${requiredOpen} Pflicht offen` : ''}</div>
    </div>
  `;
}

function renderDisplayGroup(
  g: { key: string; label: string; description?: string },
  fields: DisplayField[],
  state: EditorState,
  defaultOpen: boolean,
  mapped: number,
  total: number,
): string {
  if (fields.length === 0) return '';
  const reqOpen = fields.filter((f) => f.required && !isMapped(state.mappings.get(f.key))).length;

  return `
    <details class="kl-map-group" data-group-key="${escapeHtml(g.key)}" ${defaultOpen ? 'open' : ''}>
      <summary>
        <span class="v-icon fa-light fa-chevron-right kl-map-group-chevron"></span>
        <span class="kl-map-group-title">${escapeHtml(g.label)}</span>
        <span class="kl-map-group-meta">
          ${renderMiniProgress(mapped, total, reqOpen)}
          <span class="kl-map-group-mini-count">${mapped} / ${total}</span>
        </span>
      </summary>
      <div class="kl-map-group-body">
        ${fields.map((f) => renderFieldRow(f, state)).join('')}
      </div>
    </details>
  `;
}

function renderMiniProgress(mapped: number, total: number, requiredOpen: number): string {
  if (total === 0) return '<div class="kl-map-group-mini-progress"></div>';
  const okPct = (mapped / total) * 100;
  const warnPct = (requiredOpen / total) * 100;
  return `
    <div class="kl-map-group-mini-progress">
      <div style="background:#15803D;width:${okPct}%;"></div>
      <div style="background:var(--go-orange,#1A1D21);width:${warnPct}%;"></div>
    </div>
  `;
}

function renderFieldRow(f: DisplayField, state: EditorState): string {
  const m = state.mappings.get(f.key);
  const status = fieldStatus(f, m);
  // Beschreibung nur zeigen, wenn sie sich vom Label unterscheidet — sonst doppelt
  // (viele Kaufland-Attribute haben description == label, z.B. "Online Bestellnummer").
  const hasDistinctDesc = !!f.description && f.description.trim().toLowerCase() !== f.label.trim().toLowerCase();
  const description = hasDistinctDesc ? `<div class="kl-map-field-desc">${escapeHtml(f.description!)}</div>` : '';
  const condition = f.requiredCondition ? `<div class="kl-map-field-conditional">${escapeHtml(f.requiredCondition)}</div>` : '';
  const allowed = f.allowedValues && f.allowedValues.length > 0 ? renderAllowedValues(f) : '';
  const typeline = [
    f.dataType,
    f.maxLength ? `max ${f.maxLength}` : null,
    f.isMultilingual ? 'multilang' : null,
  ].filter(Boolean).join(' · ');
  // Push-Sektionen als Info-Icon mit Tooltip (vorher 1-N Chips pro Section).
  const aliasLabels = f.aliases.map((a) => GROUP_LABELS[a.fieldGroup] ?? a.fieldGroup);
  const uniqueAliasLabels = [...new Set(aliasLabels)];
  const sourceTooltip = uniqueAliasLabels.length > 0
    ? `Quelle: ${uniqueAliasLabels.join(', ')} · Feld-Code: ${f.key}`
    : `Feld-Code: ${f.key}`;
  // Search-Filter: data-search-label fuer simple text-match
  const searchTerm = `${f.label} ${f.key}`.toLowerCase();
  const requiredFlag = f.required ? '1' : '0';

  return `
    <div class="kl-map-field"
         data-status="${status}"
         data-field-key="${escapeHtml(f.key)}"
         data-search-label="${escapeHtml(searchTerm)}"
         data-required="${requiredFlag}">
      <div class="kl-map-field-meta">
        <div class="kl-map-field-label-row">
          <span class="kl-map-field-label" title="Feld-Code: ${escapeHtml(f.key)}">${escapeHtml(f.label)}</span>
          ${f.required ? `<span class="kl-map-field-req" title="Pflichtfeld">*</span>` : ''}
          <span class="kl-map-field-info v-icon fa-light fa-circle-info" title="${escapeHtml(sourceTooltip)}"></span>
        </div>
        <div class="kl-map-field-typeline">${escapeHtml(typeline)}</div>
        ${description}
        ${condition}
        ${allowed}
      </div>
      <div class="kl-map-field-source">
        ${renderSourceDropdown(f, state.sources, m ? sourceSelectValue(m) : '__unmapped__')}
      </div>
      <div class="kl-map-field-value-shell">
        ${renderValueInput(f, m)}
        ${renderOverrideInput(f, state.overrides.get(f.key))}
      </div>
    </div>
  `;
}

/** Listing-spezifischer Override (Migration 0129): ein fester Wert nur fuer dieses Listing,
 *  der die Profil-Quelle uebersticht. Leer = erbt Profil. Bei Enum-Feldern ein Select mit
 *  den erlaubten Werten, sonst Text/Zahl je nach dataType. Gesetzte Overrides werden
 *  hervorgehoben (orangener Rahmen), damit klar ist, dass dieses Feld vom Profil abweicht. */
function renderOverrideInput(f: DisplayField, ov: unknown): string {
  const isSet = ov !== undefined && ov !== null && ov !== '';
  const curText = ov === undefined || ov === null
    ? ''
    : (typeof ov === 'object' ? JSON.stringify(ov) : String(ov));
  const cls = `kl-map-field-override-input${isSet ? ' is-set' : ''}`;
  let input: string;
  if (f.allowedValues && f.allowedValues.length > 0) {
    const opts = [`<option value="">— erbt Profil —</option>`]
      .concat(f.allowedValues.map((av) =>
        `<option value="${escapeHtml(av.value)}"${av.value === curText ? ' selected' : ''}>${escapeHtml(av.label ?? av.value)}</option>`));
    input = `<select class="${cls}" data-field-override="${escapeHtml(f.key)}">${opts.join('')}</select>`;
  } else {
    const type = (f.dataType === 'currency_cents' || f.dataType === 'integer' || f.dataType === 'float') ? 'number' : 'text';
    input = `<input type="${type}" class="${cls}" data-field-override="${escapeHtml(f.key)}" value="${escapeHtml(curText)}" placeholder="eigener Wert (erbt sonst Profil)" />`;
  }
  return `
    <div class="kl-map-field-override"
         title="Eigener Wert nur für diesen Artikel — sticht die Profil-Quelle. Leer = erbt das Profil-Mapping.">
      ${input}
      <div class="kl-map-field-override-cap${isSet ? ' is-set' : ''}">${isSet ? '✓ nur dieser Artikel' : 'nur dieser Artikel'}</div>
    </div>`;
}

function sourceSelectValue(m: ExistingFieldMapping): string {
  if (m.source_type === 'fixed_value') return '__fixed_value__';
  if (m.source_type === 'template') return '__template__';
  if (m.source_type === 'vario_path' && m.vario_path) return m.vario_path;
  if (m.source_type === 'vario_dms_resource' && m.vario_resource_id) {
    return `article.dms.image_${m.vario_resource_id}`;
  }
  return '__unmapped__';
}

function renderSourceDropdown(_f: SchemaField, sources: SourcesResp, selectedValue: string): string {
  const opts: string[] = [`<option value="__unmapped__">— nicht gemappt —</option>`];
  for (const group of sources.groups) {
    if (group.sources.length === 0) continue;
    opts.push(`<optgroup label="${escapeHtml(group.label)}">`);
    for (const s of group.sources) {
      const selected = s.sourceKey === selectedValue ? ' selected' : '';
      const ex = s.exampleValue !== undefined && s.exampleValue !== null && String(s.exampleValue).length > 0
        ? ` — ${String(s.exampleValue)}`
        : '';
      opts.push(`<option value="${escapeHtml(s.sourceKey)}"${selected}>${escapeHtml(s.label)}${escapeHtml(ex)}</option>`);
    }
    opts.push(`</optgroup>`);
  }
  return `<select data-field-source="${escapeHtml(_f.key)}">${opts.join('')}</select>`;
}

function renderValueInput(f: SchemaField, m: ExistingFieldMapping | undefined): string {
  if (!m || m.source_type === 'unmapped') {
    // Kein „—"-Platzhalter mehr — Spalte 2 zeigt bereits „nicht gemappt", Spalte 3 bleibt
    // allein der Override-Eingabe vorbehalten.
    return '';
  }
  if (m.source_type === 'fixed_value') {
    const raw = m.fixed_value;
    const str = raw === null || raw === undefined ? '' : typeof raw === 'string' ? raw : JSON.stringify(raw);

    // Bei enum-feldern mit allowedValues (z.B. vat_indicator, condition, id_shipping_group)
    // rendern wir ein select-dropdown statt freiem text-input — verhindert tippfehler und
    // erspart dem user das nachschlagen der erlaubten codes.
    if (f.allowedValues && f.allowedValues.length > 0) {
      const opts = f.allowedValues.map((av) => {
        const sel = av.value === str ? ' selected' : '';
        return `<option value="${escapeHtml(av.value)}"${sel}>${escapeHtml(av.label ?? av.value)}</option>`;
      }).join('');
      return `
        <select data-field-value="${escapeHtml(f.key)}">
          <option value="">— bitte wählen —</option>
          ${opts}
        </select>
        <div class="kl-map-field-value-hint">Direkter Wert aus der vorgegebenen Liste.</div>
      `;
    }

    return `
      <input type="text" data-field-value="${escapeHtml(f.key)}" value="${escapeHtml(str)}" placeholder="Fester Wert …" />
      <div class="kl-map-field-value-hint">Hardcode-Wert für jeden Push.</div>
    `;
  }
  if (m.source_type === 'template') {
    return `
      <textarea data-field-value="${escapeHtml(f.key)}" rows="2" placeholder="z.B. {listing.name} ({article.manufacturerRefLabel})">${escapeHtml(m.template ?? '')}</textarea>
      <div class="kl-map-field-value-hint">Platzhalter <code>{sourceKey}</code> → Preview resolved gegen Beispielartikel.</div>
    `;
  }
  if (m.source_type === 'vario_dms_resource') {
    return `
      <div class="kl-map-field-value-vario">
        <span class="v-icon fa-light fa-image"></span>
        <code>DMS: ${escapeHtml(m.vario_resource_id ?? '')}</code>
      </div>
      <div class="kl-map-field-value-hint">Bild aus dem Vario-DMS — Public-URL wird beim Push automatisch eingesetzt.</div>
    `;
  }
  // vario_path: kein blauer Quell-Pfad-Chip mehr (z.B. listing.ean/listing.name) — die
  // gewaehlte Quelle steht bereits im Mapping-Dropdown (Spalte 2). Spalte 3 ("manuelle
  // Eingabe") bleibt allein dem Listing-Override vorbehalten.
  return '';
}

function renderAllowedValues(f: SchemaField): string {
  if (!f.allowedValues || f.allowedValues.length === 0) return '';
  const labels = f.allowedValues.slice(0, 3).map((v) => v.label ?? v.value).join(' · ');
  const extra = f.allowedValues.length > 3 ? ` · +${f.allowedValues.length - 3} weitere` : '';
  return `<div class="kl-map-field-allowed">Erlaubte Werte: ${escapeHtml(labels)}${extra}</div>`;
}

function renderAlert(level: 'info' | 'warn' | 'error', message: string): string {
  const cls = level === 'error' ? 'v-alert--error' : level === 'warn' ? 'v-alert--warn' : 'v-alert--info';
  return `<div class="v-alert ${cls}" style="font-size:0.85em;padding:0.5em 0.8em;">${escapeHtml(message)}</div>`;
}

// ---------- State helpers ----------

function isMapped(m: ExistingFieldMapping | undefined): boolean {
  if (!m) return false;
  if (m.source_type === 'unmapped') return false;
  if (m.source_type === 'fixed_value') return m.fixed_value !== null && m.fixed_value !== undefined && m.fixed_value !== '';
  if (m.source_type === 'template') return Boolean(m.template && m.template.length > 0);
  if (m.source_type === 'vario_path') return Boolean(m.vario_path);
  if (m.source_type === 'vario_dms_resource') return Boolean(m.vario_resource_id);
  return false;
}

function countMapped(state: EditorState): number {
  let n = 0;
  for (const f of state.displayFields) if (isMapped(state.mappings.get(f.key))) n += 1;
  return n;
}

function countRequiredOpen(state: EditorState): number {
  let n = 0;
  for (const f of state.displayFields) if (f.required && !isMapped(state.mappings.get(f.key))) n += 1;
  return n;
}

function fieldStatus(f: DisplayField, m: ExistingFieldMapping | undefined): string {
  if (f.required && !isMapped(m)) return 'required-empty';
  if (!m || m.source_type === 'unmapped') return 'unmapped';
  if (m.source_type === 'fixed_value') return 'fixed';
  if (m.source_type === 'template') return 'template';
  if (m.source_type === 'vario_dms_resource') return 'mapped';
  return 'mapped';
}

// ---------- Event wiring ----------

function wireEvents(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState): void {
  const groupsHost = host.querySelector<HTMLElement>('#kl-map-groups')!;

  groupsHost.querySelectorAll<HTMLSelectElement>('select[data-field-source]').forEach((sel) => {
    sel.addEventListener('change', () => onSourceChange(host, state, sel.dataset['fieldSource']!, sel.value));
  });
  groupsHost.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-field-value]').forEach((inp) => {
    // Select-elemente feuern `change`, text-inputs/textarea `input`. Beide binden — bei
    // text-inputs ist `change` zusaetzlich (feuert beim blur), bei selects ist `input`
    // nicht zuverlaessig. Wir rufen onValueChange in beiden faellen; ist idempotent.
    const handler = () => onValueChange(host, state, inp.dataset['fieldValue']!, (inp as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value);
    inp.addEventListener('input', handler);
    inp.addEventListener('change', handler);
  });

  // Listing-Override-Inputs (Migration 0129): delegiert auf groupsHost, damit der Listener
  // auch nach rerenderRow (Quellen-Wechsel ersetzt eine Row) erhalten bleibt. Nur `change`
  // (nicht `input`) — ein RPC pro Tastendruck waere zu viel; change feuert bei blur/commit.
  groupsHost.addEventListener('change', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.matches !== 'function' || !t.matches('[data-field-override]')) return;
    const el = t as HTMLInputElement | HTMLSelectElement;
    void saveOverride(ctx, listing, state, el.dataset['fieldOverride']!, el.value, el);
  });

  host.querySelector<HTMLButtonElement>('[data-action="save"]')!.addEventListener('click', () => void saveProfile(host, ctx, listing, state));
  host.querySelector<HTMLButtonElement>('[data-action="reset"]')!.addEventListener('click', () => void renderMappingTab(host, ctx, listing));
  host.querySelector<HTMLButtonElement>('[data-action="preview"]')!.addEventListener('click', () => void requestPreview(host, ctx, listing, state));
  // Sticky-Bar-Buttons spiegeln die Header-Aktionen (gleicher Handler)
  host.querySelector<HTMLButtonElement>('[data-action="save-bottom"]')?.addEventListener('click', () => void saveProfile(host, ctx, listing, state));
  host.querySelector<HTMLButtonElement>('[data-action="reset-bottom"]')?.addEventListener('click', () => void renderMappingTab(host, ctx, listing));
  host.querySelector<HTMLButtonElement>('[data-action="preview-bottom"]')?.addEventListener('click', () => void requestPreview(host, ctx, listing, state));

  // Quick-Filter: Live-Such-Filter
  const searchInput = host.querySelector<HTMLInputElement>('#kl-map-search');
  searchInput?.addEventListener('input', () => applyQuickFilter(host, searchInput.value, isOnlyRequiredOpenActive(host)));
  // Quick-Filter: Toggle "Nur Pflicht offen"
  const toggleBtn = host.querySelector<HTMLButtonElement>('#kl-map-toggle-required');
  toggleBtn?.addEventListener('click', () => {
    const active = toggleBtn.dataset['active'] === '1';
    toggleBtn.dataset['active'] = active ? '0' : '1';
    applyQuickFilter(host, searchInput?.value ?? '', !active);
  });
  // Auto-Map-Vorschläge — zweistufige Bestätigung (Projektregel: keine window.confirm)
  const autoMapBtn = host.querySelector<HTMLButtonElement>('#kl-map-automap');
  if (autoMapBtn) {
    wireTwoStepConfirm(
      autoMapBtn,
      `<span class="v-icon fa-regular fa-wand-magic-sparkles"></span> Vorschläge wirklich anwenden?`,
      () => void runAutoMap(host, state),
    );
  }

  updateSaveButton(host, state);
}

function isOnlyRequiredOpenActive(host: HTMLElement): boolean {
  return host.querySelector<HTMLButtonElement>('#kl-map-toggle-required')?.dataset['active'] === '1';
}

/** Show/hide field rows based on search term + "only required open" toggle.
 *  Reine DOM-Operation, keine state-Mutation — Filter ist nur Anzeige-Filter. */
function applyQuickFilter(host: HTMLElement, searchTerm: string, onlyRequiredOpen: boolean): void {
  const term = searchTerm.trim().toLowerCase();
  host.querySelectorAll<HTMLElement>('.kl-map-field').forEach((row) => {
    const label = row.dataset['searchLabel'] ?? '';
    const required = row.dataset['required'] === '1';
    const status = row.dataset['status'] ?? '';
    let visible = true;
    if (term && !label.includes(term)) visible = false;
    if (onlyRequiredOpen && !(required && status === 'required-empty')) visible = false;
    row.hidden = !visible;
  });
}

/** Auto-Map: fuer jedes nicht-gemappte Field versuchen, eine Source zu finden, deren
 *  letztes Pfad-Segment case-insensitive zum Field-Code matched (z.B. field 'ean' →
 *  source 'article.ean'). Vor dem Anwenden: zweistufiger Klick am Button. Toast danach. */
function runAutoMap(host: HTMLElement, state: EditorState): void {
  const candidates: Array<{ fieldKey: string; sourceKey: string }> = [];
  for (const f of state.displayFields) {
    if (isMapped(state.mappings.get(f.key))) continue;
    const target = f.key.toLowerCase();
    let found: string | null = null;
    outer: for (const group of state.sources.groups) {
      for (const s of group.sources) {
        const lastSeg = s.sourceKey.split('.').pop()?.toLowerCase() ?? '';
        if (lastSeg === target) { found = s.sourceKey; break outer; }
      }
    }
    if (found) candidates.push({ fieldKey: f.key, sourceKey: found });
  }
  if (candidates.length === 0) {
    showToast('Keine Auto-Map-Vorschläge gefunden.', 'info');
    return;
  }

  // Mappings anwenden ohne re-render — onSourceChange erledigt das pro Field.
  for (const c of candidates) {
    const sel = host.querySelector<HTMLSelectElement>(`select[data-field-source="${cssEscape(c.fieldKey)}"]`);
    if (!sel) continue;
    sel.value = c.sourceKey;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }
  showToast(`${candidates.length} Felder automatisch gemappt`, 'success');
}

async function requestPreview(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState): Promise<void> {
  // Vorschau zeigt den gespeicherten profil-stand. Bei dirty zuerst speichern, sonst sieht
  // der user veralteten kram. Save-failure stoppt den tab-switch — der user soll erst den
  // fehler beheben.
  if (state.dirty) {
    const ok = await saveProfile(host, ctx, listing, state, { silent: true });
    if (!ok) return;
  }
  if (ctx.onPreviewRequested) ctx.onPreviewRequested();
  else showToast('Kein Vorschau-Handler verfuegbar.', 'error');
}

function onSourceChange(host: HTMLElement, state: EditorState, fieldKey: string, newSourceKey: string): void {
  const displayField = state.displayFields.find((f) => f.key === fieldKey);
  if (!displayField) return;
  const fg = displayField.originalFieldGroup;
  // slot_index: bisher fuer picture_<n> verwendet (mittlerweile entfernt). Felder ohne
  // slot-spezifische Semantik bekommen null — die DB-Spalte ist NULLable.
  const slotIdx: number | null = null;
  let mapping: ExistingFieldMapping;
  if (newSourceKey === '__unmapped__') {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'unmapped', vario_path: null, fixed_value: null, template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else if (newSourceKey === '__fixed_value__') {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'fixed_value', vario_path: null, fixed_value: '', template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else if (newSourceKey === '__template__') {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'template', vario_path: null, fixed_value: null, template: '', fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'vario_path', vario_path: newSourceKey, fixed_value: null, template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  }
  state.mappings.set(fieldKey, mapping);
  state.dirty = true;
  rerenderRow(host, state, fieldKey);
  updateHeaderProgress(host, state);
  updateSaveButton(host, state);
}

function onValueChange(host: HTMLElement, state: EditorState, fieldKey: string, newValue: string): void {
  const m = state.mappings.get(fieldKey);
  if (!m) return;
  if (m.source_type === 'fixed_value') m.fixed_value = newValue;
  else if (m.source_type === 'template') m.template = newValue;
  else return;
  state.dirty = true;
  // Status-Stripe + Header-Progress aktualisieren ohne Input-Refocus zu zerstoeren.
  // Bei select-elementen (enum-feldern) ist das gewollt; bei freien text-inputs/textarea
  // wuerde ein re-render den focus zerstoeren, daher hier nur das dataset.status mutieren.
  const row = host.querySelector<HTMLElement>(`.kl-map-field[data-field-key="${cssEscape(fieldKey)}"]`);
  if (row) row.dataset['status'] = fieldStatus(state.displayFields.find((f) => f.key === fieldKey)!, m);
  updateHeaderProgress(host, state);
  updateSaveButton(host, state);
}

/** Listing-Override (Migration 0129) sofort speichern — eigene RPC, unabhaengig vom
 *  Profil-Save-Button. Leerer Wert loescht den Override (Feld erbt wieder das Profil).
 *  Zahlenfelder werden als jsonb-Number gespeichert, sonst als String. */
async function saveOverride(
  ctx: MappingTabCtx,
  listing: MappingTabListing,
  state: EditorState,
  fieldKey: string,
  raw: string,
  el: HTMLInputElement | HTMLSelectElement,
): Promise<void> {
  const f = state.displayFields.find((x) => x.key === fieldKey);
  let value: unknown;
  if (raw === '') {
    value = null;
  } else if (f && (f.dataType === 'currency_cents' || f.dataType === 'integer' || f.dataType === 'float')) {
    const n = Number(raw);
    value = Number.isFinite(n) ? n : raw;
  } else {
    value = raw;
  }
  const { error } = await ctx.supabase.rpc('kaufland_set_listing_field_override', {
    _storefront: listing.storefront,
    _vario_article_id: listing.vario_article_id,
    _kaufland_field_key: fieldKey,
    _value: value,
  });
  if (error) {
    showToast(`Override speichern fehlgeschlagen: ${error.message}`, 'error');
    return;
  }
  if (value === null) state.overrides.delete(fieldKey);
  else state.overrides.set(fieldKey, value);
  // Visuelles Feedback: gesetzter Override = orangener Rahmen + hervorgehobene Caption.
  const set = value !== null;
  el.classList.toggle('is-set', set);
  const cap = el.parentElement?.querySelector('.kl-map-field-override-cap');
  if (cap) {
    cap.classList.toggle('is-set', set);
    cap.textContent = set ? '✓ nur dieser Artikel' : 'nur dieser Artikel';
  }
  showToast(set ? 'Override gespeichert (nur dieser Artikel)' : 'Override entfernt — erbt wieder das Profil', 'success');
}

function rerenderRow(host: HTMLElement, state: EditorState, fieldKey: string): void {
  const oldRow = host.querySelector<HTMLElement>(`.kl-map-field[data-field-key="${cssEscape(fieldKey)}"]`);
  if (!oldRow) return;
  const displayField = state.displayFields.find((f) => f.key === fieldKey);
  if (!displayField) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderFieldRow(displayField, state);
  const newRow = tmp.firstElementChild as HTMLElement | null;
  if (!newRow) return;
  oldRow.replaceWith(newRow);
  // Events neu binden — nur fuer diese eine row.
  newRow.querySelectorAll<HTMLSelectElement>('select[data-field-source]').forEach((sel) => {
    sel.addEventListener('change', () => onSourceChange(host, state, sel.dataset['fieldSource']!, sel.value));
  });
  newRow.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-field-value]').forEach((inp) => {
    const handler = () => onValueChange(host, state, inp.dataset['fieldValue']!, (inp as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value);
    inp.addEventListener('input', handler);
    inp.addEventListener('change', handler);
  });
}

function updateHeaderProgress(host: HTMLElement, state: EditorState): void {
  const mapped = countMapped(state);
  const reqOpen = countRequiredOpen(state);
  const total = state.displayFields.length;

  // Header-Progress-Row: Bar + Text — wenn Container existiert, neu rendern.
  const progressRow = host.querySelector<HTMLElement>('.kl-map-progress-row');
  if (progressRow) {
    const okPct = total > 0 ? (mapped / total) * 100 : 0;
    const warnPct = total > 0 ? (reqOpen / total) * 100 : 0;
    progressRow.innerHTML = `
      <div class="kl-map-progress-bar" role="progressbar" aria-valuenow="${mapped}" aria-valuemin="0" aria-valuemax="${total}">
        <div class="kl-map-progress-seg-ok" style="width: ${okPct}%;"></div>
        <div class="kl-map-progress-seg-warn" style="width: ${warnPct}%;"></div>
      </div>
      <div class="kl-map-progress-text">${mapped} / ${total} Felder gemappt${reqOpen > 0 ? ` · ${reqOpen} Pflicht offen` : ''}</div>
    `;
  }

  // Pro Section: Mini-Progress + Count
  // Aufteilung MUSS spiegeln, was renderEditor() macht — sonst zaehlt das Vario-
  // Gruppen-Mini-Progress falsch. Vario-Felder werden vor Pflicht/Optional extrahiert.
  const varioFields = state.displayFields.filter((f) => isVarioField(f));
  const remainingFields = state.displayFields.filter((f) => !isVarioField(f));
  const requiredFields = remainingFields.filter((f) => f.required);
  const optionalFields = remainingFields.filter((f) => !f.required);
  host.querySelectorAll<HTMLElement>('.kl-map-group').forEach((groupEl) => {
    const groupKey = groupEl.dataset['groupKey'];
    if (!groupKey) return;
    const fields = groupKey === 'required' ? requiredFields
      : groupKey === 'vario' ? varioFields
      : groupKey === 'optional' ? optionalFields
      : [];
    const groupMeta = groupEl.querySelector<HTMLElement>('.kl-map-group-meta');
    if (!groupMeta) return;
    const groupMapped = fields.filter((f) => isMapped(state.mappings.get(f.key))).length;
    const groupReqOpen = fields.filter((f) => f.required && !isMapped(state.mappings.get(f.key))).length;
    groupMeta.innerHTML = `
      ${renderMiniProgress(groupMapped, fields.length, groupReqOpen)}
      <span class="kl-map-group-mini-count">${groupMapped} / ${fields.length}</span>
    `;
  });
}

function updateSaveButton(host: HTMLElement, state: EditorState): void {
  for (const id of ['#kl-map-save', '#kl-map-save-bottom']) {
    const btn = host.querySelector<HTMLButtonElement>(id);
    if (!btn) continue;
    btn.disabled = false;
    btn.classList.toggle('kl-map-save-dirty', state.dirty);
    btn.innerHTML = `<span class="v-icon fa-light fa-floppy-disk"></span>Speichern`;
  }
}

// ---------- Save ----------

async function saveProfile(
  host: HTMLElement,
  ctx: MappingTabCtx,
  listing: MappingTabListing,
  state: EditorState,
  opts: { silent?: boolean } = {},
): Promise<boolean> {
  const categoryId = listing.kaufland_category_id;
  if (!categoryId) return false;
  const storefront = listing.storefront;
  const locale = localeFor(storefront);

  // Save: eine Row pro display-key (DB-Constraint UNIQUE(profile_id, kaufland_field_key)).
  // Bei Multi-Target-Feldern (z.B. EAN -> product_data + unit) bedient der Push-Service
  // das fan-out automatisch via spezialroute in kaufland-listing-preview.
  const fieldsPayload = [];
  for (const df of state.displayFields) {
    const m = state.mappings.get(df.key);
    if (!m || m.source_type === 'unmapped') continue;
    fieldsPayload.push({
      kaufland_field_key: df.key,
      field_group: df.originalFieldGroup,
      source_type: m.source_type,
      vario_path: m.vario_path,
      fixed_value: m.fixed_value,
      template: m.template,
      fallback: m.fallback,
      vario_resource_id: m.vario_resource_id ?? null,
      slot_index: m.slot_index ?? null,
    });
  }

  const saveBtns = host.querySelectorAll<HTMLButtonElement>('#kl-map-save, #kl-map-save-bottom');
  saveBtns.forEach((b) => {
    b.disabled = true;
    b.innerHTML = `<span class="v-icon fa-light fa-spinner-third fa-spin"></span>Speichere …`;
  });

  const { error } = await ctx.supabase.rpc('kaufland_save_mapping_profile', {
    _storefront: storefront,
    _locale: locale,
    _category_id: categoryId,
    _fields: fieldsPayload,
    _variants: [],
  });

  if (error) {
    showToast(`Speichern fehlgeschlagen: ${error.message}`, 'error');
    saveBtns.forEach((b) => {
      b.disabled = false;
      b.innerHTML = `<span class="v-icon fa-light fa-floppy-disk"></span>Speichern`;
    });
    return false;
  }

  if (!opts.silent) showToast(`Mapping gespeichert (${fieldsPayload.length} Felder).`, 'success');
  state.dirty = false;
  // Beim silent-save (z.B. via Vorschau-Button) nicht neu rendern — der Parent-Tab schaltet
  // gleich auf preview, ein zwischenzeitlicher mapping-rerender waere flackernd.
  if (!opts.silent) await renderMappingTab(host, ctx, listing);
  return true;
}

// ---------- Helpers ----------

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function cssEscape(s: string): string {
  // Minimaler escaper für attribute-selectors. Deckt unsere field-keys ab (ascii + dots).
  return s.replace(/(["\\])/g, '\\$1');
}
